import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Checkbox
} from '@mui/material';
import { Add, Refresh, Visibility, Edit, GetApp, TableChart, Search, DeleteOutline } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/apiErrorUtils';
import { ROLES } from '../../constants/roles';
import { formatDateWithTimezone } from '../../utils/timezoneUtils';
import { canEditPurchaseOrder, canSuperAdminDeletePurchaseOrder } from '../../utils/purchaseOrderPermissions';
import PurchaseOrderDeleteDialog from './PurchaseOrderDeleteDialog';

const PO_STATUS_OPTIONS = [
  'DRAFT',
  'PENDING_STORE_APPROVAL',
  'PENDING_AM_APPROVAL',
  'ISSUED',
  'PARTIALLY_RECEIVED',
  'RECEIVED',
  'CLOSED',
  'CANCELLED'
];

const statusColor = (status) => {
  const map = {
    DRAFT: 'default',
    PENDING_STORE_APPROVAL: 'warning',
    PENDING_AM_APPROVAL: 'warning',
    ISSUED: 'info',
    PARTIALLY_RECEIVED: 'primary',
    RECEIVED: 'success',
    CLOSED: 'default',
    CANCELLED: 'error'
  };
  return map[status] || 'default';
};

const PurchaseOrderList = ({
  onView,
  onEdit,
  onCreate,
  displayTimezone,
  filters,
  onFiltersChange
}) => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [partNumberInput, setPartNumberInput] = useState(filters.partNumber || '');
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, targets: [] });

  const canCreatePo = [ROLES.STORE_EXECUTIVE, ROLES.STORE_INSPECTOR, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role);
  const isSuperAdmin = canSuperAdminDeletePurchaseOrder(user?.role);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status && filters.status !== 'ALL') params.status = filters.status;
      if (filters.supplierId) params.supplierId = filters.supplierId;
      if (filters.aircraftId) params.aircraftId = filters.aircraftId;
      if (filters.search?.trim()) params.search = filters.search.trim();
      if (filters.partNumber?.trim()) params.partNumber = filters.partNumber.trim();
      const response = await axios.get('/api/store/purchase-orders', { params });
      setList(response.data || []);
    } catch (error) {
      console.error('Failed to load purchase orders', error);
      showToast(getErrorMessage(error, 'Failed to load purchase orders.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.supplierId, filters.aircraftId, filters.search, filters.partNumber, showToast]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const visibleIds = new Set(list.map((po) => String(po._id)));
    setSelectedIds((prev) => prev.filter((id) => visibleIds.has(String(id))));
  }, [list]);

  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  useEffect(() => {
    setPartNumberInput(filters.partNumber || '');
  }, [filters.partNumber]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = searchInput.trim();
      onFiltersChange((prev) => {
        if (next === String(prev.search || '').trim()) return prev;
        return { ...prev, search: next };
      });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput, onFiltersChange]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = partNumberInput.trim();
      onFiltersChange((prev) => {
        if (next === String(prev.partNumber || '').trim()) return prev;
        return { ...prev, partNumber: next };
      });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [partNumberInput, onFiltersChange]);

  useEffect(() => {
    const load = async () => {
      try {
        const [supRes, acRes] = await Promise.all([
          axios.get('/api/master-data/agencies', {
            params: { isActive: 'true', type: 'STORE_SUPPLIER' }
          }),
          axios.get('/api/aircraft')
        ]);
        setSuppliers(supRes.data || []);
        const acData = acRes.data;
        setAircraft(Array.isArray(acData) ? acData : (acData?.aircraft || acData?.data || []));
      } catch (e) {
        console.error('Failed to load filter options', e);
      }
    };
    load();
  }, []);

  const handleExportCsv = async (id, poNumber) => {
    try {
      const res = await axios.get(`/api/store/purchase-orders/${id}/export/csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `PO-${poNumber || id}.csv`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast('CSV exported', 'success');
    } catch (error) {
      showToast(getErrorMessage(error, 'Export failed.'), 'error');
    }
  };

  const selectedCount = selectedIds.length;
  const allVisibleSelected = list.length > 0 && list.every((po) => selectedIds.includes(String(po._id)));
  const someVisibleSelected = list.some((po) => selectedIds.includes(String(po._id)));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(list.map((po) => String(po._id)));
  };

  const toggleSelectOne = (poId) => {
    const id = String(poId);
    setSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const openDeleteDialog = (targets) => {
    if (!targets.length) return;
    setDeleteDialog({ open: true, targets });
  };

  const handleExportExcel = async (id, poNumber) => {
    try {
      const res = await axios.get(`/api/store/purchase-orders/${id}/export/xlsx`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `PO-${poNumber || id}.xlsx`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast('Excel exported', 'success');
    } catch (error) {
      showToast(getErrorMessage(error, 'Excel export failed.'), 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Purchase Orders</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {isSuperAdmin && selectedCount > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={() => {
                const targets = list.filter((po) => selectedIds.includes(String(po._id)));
                openDeleteDialog(targets);
              }}
            >
              Delete selected ({selectedCount})
            </Button>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={fetchList} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          {canCreatePo && onCreate && (
            <Button variant="contained" startIcon={<Add />} onClick={onCreate}>
              New Purchase Order
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search PO number"
            placeholder="Enter PO number…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <TextField
            size="small"
            label="Search part number"
            placeholder="Enter part number…"
            value={partNumberInput}
            onChange={(e) => setPartNumberInput(e.target.value)}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
            >
              <MenuItem value="ALL">All</MenuItem>
              {PO_STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Supplier</InputLabel>
            <Select
              value={filters.supplierId}
              label="Supplier"
              onChange={(e) => onFiltersChange({ ...filters, supplierId: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {suppliers.map((s) => (
                <MenuItem key={s._id} value={s._id}>{s.name} ({s.code})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Aircraft</InputLabel>
            <Select
              value={filters.aircraftId || ''}
              label="Aircraft"
              onChange={(e) => onFiltersChange({ ...filters, aircraftId: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="GEN">GEN (General stock)</MenuItem>
              {aircraft.map((ac) => (
                <MenuItem key={ac._id} value={ac._id}>
                  {ac.registration || ac._id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(filters.search || filters.partNumber || filters.aircraftId || filters.supplierId || filters.status !== 'ALL') && (
            <Button
              size="small"
              onClick={() => {
                setSearchInput('');
                setPartNumberInput('');
                onFiltersChange({ status: 'ALL', supplierId: '', aircraftId: '', search: '', partNumber: '' });
              }}
            >
              Clear filters
            </Button>
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {isSuperAdmin && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected && !allVisibleSelected}
                    onChange={toggleSelectAll}
                    disabled={loading || list.length === 0}
                    inputProps={{ 'aria-label': 'Select all purchase orders' }}
                  />
                </TableCell>
              )}
              <TableCell>PO Number</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Order To</TableCell>
              <TableCell>Aircraft</TableCell>
              <TableCell>Expected Delivery</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isSuperAdmin ? 9 : 8} sx={{ textAlign: 'center', py: 3 }}>Loading…</TableCell>
              </TableRow>
            ) : list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isSuperAdmin ? 9 : 8} sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">No purchase orders found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              list.map((po) => (
                <TableRow
                  key={po._id}
                  selected={isSuperAdmin && selectedIds.includes(String(po._id))}
                  hover
                >
                  {isSuperAdmin && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(String(po._id))}
                        onChange={() => toggleSelectOne(po._id)}
                        inputProps={{ 'aria-label': `Select purchase order ${po.poNumber}` }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{po.poNumber}</TableCell>
                  <TableCell>
                    <Chip label={po.status} size="small" color={statusColor(po.status)} />
                  </TableCell>
                  <TableCell>{po.priority === 'CRITICAL' ? 'AOG' : po.priority === 'HIGH' ? 'Rush' : po.priority === 'MEDIUM' ? 'Normal' : (po.priority || '—')}</TableCell>
                  <TableCell>{po.orderTo?.name || '—'}</TableCell>
                  <TableCell>{po.aircraftRegistration || po.aircraftId?.registration || '—'}</TableCell>
                  <TableCell>
                    {po.expectedDeliveryDate ? (displayTimezone ? formatDateWithTimezone(po.expectedDeliveryDate, displayTimezone) : new Date(po.expectedDeliveryDate).toLocaleDateString()) : '—'}
                  </TableCell>
                  <TableCell>{po.createdAt ? new Date(po.createdAt).toLocaleDateString() : '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => onView && onView(po._id)}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    {canEditPurchaseOrder(user?.role, po.status) && onEdit && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEdit(po._id)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Export CSV">
                      <IconButton size="small" onClick={() => handleExportCsv(po._id, po.poNumber)}>
                        <GetApp />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel (letterpad + table)">
                      <IconButton size="small" color="primary" onClick={() => handleExportExcel(po._id, po.poNumber)}>
                        <TableChart />
                      </IconButton>
                    </Tooltip>
                    {isSuperAdmin && (
                      <Tooltip title="Delete purchase order">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeleteDialog([po])}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PurchaseOrderDeleteDialog
        open={deleteDialog.open}
        targets={deleteDialog.targets}
        onClose={() => setDeleteDialog({ open: false, targets: [] })}
        onDeleted={() => {
          setDeleteDialog({ open: false, targets: [] });
          setSelectedIds([]);
          fetchList();
        }}
      />
    </Box>
  );
};

export default PurchaseOrderList;
